// Google Places API (New), pure fetch, no extra libraries.
//
// REACT_APP_GOOGLE_PLACES_API_KEY comes from .env. In Google Cloud Console the
// key is restricted to HTTP referrers (our domains) with "Places API (New)"
// enabled. It is read per call rather than once at import so tests can set it;
// CRA inlines it at build time either way.
//
// Every method resolves, never rejects: a missing key or a failed request comes
// back as the empty value ([] or null), which is what the callers already
// render as "no results".

const PLACES_BASE_URL = "https://places.googleapis.com/v1";

const apiKey = () => process.env.REACT_APP_GOOGLE_PLACES_API_KEY;

class PlacesAPI {
  /**
   * Autocomplete predictions for a typed string. `includedPrimaryTypes` is
   * optional; without it the request carries only `input`.
   */
  static async autocomplete(input, { includedPrimaryTypes } = {}) {
    const key = apiKey();
    if (!input || input.length < 2 || !key) return [];
    try {
      const res = await fetch(`${PLACES_BASE_URL}/places:autocomplete`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-Goog-Api-Key": key,
        },
        body: JSON.stringify({ input, includedPrimaryTypes }),
      });
      const data = await res.json();
      return data.suggestions || [];
    } catch (err) {
      console.error("[PlacesAPI] autocomplete error:", err);
      return [];
    }
  }

  /** `{ lat, lng }` for a place id, or null. */
  static async getLocation(placeId) {
    const key = apiKey();
    if (!placeId || !key) return null;
    try {
      const res = await fetch(`${PLACES_BASE_URL}/places/${placeId}`, {
        headers: {
          "X-Goog-Api-Key": key,
          "X-Goog-FieldMask": "location",
        },
      });
      const data = await res.json();
      if (data.location?.latitude != null) {
        return { lat: data.location.latitude, lng: data.location.longitude };
      }
      return null;
    } catch (err) {
      console.error("[PlacesAPI] place details error:", err);
      return null;
    }
  }

  /**
   * The given place id when there is one. Otherwise a Text Search on the venue
   * name + address, returning the first hit's id, or null.
   */
  static async resolvePlaceId(placeId, venueName, venueAddress) {
    if (placeId) return placeId;
    const key = apiKey();
    if (!venueName || !key) return null;
    try {
      const textQuery = [venueName, venueAddress].filter(Boolean).join(", ");
      const res = await fetch(`${PLACES_BASE_URL}/places:searchText`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-Goog-Api-Key": key,
          "X-Goog-FieldMask": "places.id",
        },
        body: JSON.stringify({ textQuery }),
      });
      const data = await res.json();
      return data.places?.[0]?.id || null;
    } catch (err) {
      console.error("[PlacesAPI] text search error:", err);
      return null;
    }
  }

  /**
   * Up to 10 venue photos, each resolved to a permanent Google CDN photoUri
   * (no API key embedded in the URL we store).
   */
  static async getPhotoUris(placeId) {
    const key = apiKey();
    if (!placeId || !key) return [];
    try {
      // Step 1: photo references via Place Details
      const detailsRes = await fetch(`${PLACES_BASE_URL}/places/${placeId}?fields=photos`, {
        headers: { "X-Goog-Api-Key": key, "X-Goog-FieldMask": "photos" },
      });
      const details = await detailsRes.json();
      const photos = details.photos || [];

      // Step 2: resolve each photo name to its CDN photoUri
      const urls = await Promise.all(
        photos.slice(0, 10).map(async (photo) => {
          const mediaRes = await fetch(
            `${PLACES_BASE_URL}/${photo.name}/media?maxWidthPx=1200&skipHttpRedirect=true`,
            { headers: { "X-Goog-Api-Key": key } }
          );
          const mediaData = await mediaRes.json();
          return mediaData.photoUri || null;
        })
      );
      return urls.filter(Boolean);
    } catch (err) {
      console.error("[PlacesAPI] photo fetch error:", err);
      return [];
    }
  }
}

export default PlacesAPI;
